import * as React from 'react'
import { format } from 'date-fns'
import { useCapability } from '@/features/permissions/hooks'
import { getAppointmentDate } from '../utils/calendarAppointments'
import { Link as RouterLink } from 'react-router-dom'
import { ChevronDown, ChevronUp, Link, X } from 'lucide-react'
import { toast } from 'sonner'

import { useDataTable } from '@/components/data-table/data-table-provider'
import { useSheetRowNavigation } from '@/hooks/useSheetRowNavigation'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type { AppointmentRecord } from '@/features/appointments/types'
import {
  sourceDisplayName,
  formatAppointmentDate,
  formatAppointmentTime,
} from './AppointmentsColumns'
import { AppointmentsClientIdentifiers } from './AppointmentsClientIdentifiers'
import { AppointmentStatusBadge } from './AppointmentStatusBadge'
import { AppointmentsSheetSectionCard } from './AppointmentsSheetSectionCard'
import { isOpenSlotRow } from '../utils/openSlotRows'
// Derived from the table id rather than spelled out here — the table
// namespaces its URL params, so this must track `namespaceUrlKeys`.
import { APPOINTMENT_ID_PARAM } from './appointmentsTableSchema'
import { patientDetailsHref } from '@/features/patients/components/patientsTableSchema'

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  const empty =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim().length === 0)
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      {/* No truncate: long reasons / source names wrap so the sheet always
          reveals the fields the 6-column table intentionally omits. */}
      <span className="min-w-0 text-right break-words">
        {empty ? '-' : value}
      </span>
    </div>
  )
}

function MetaCard({
  title,
  rows,
}: {
  title: string
  rows: { label: string; value: React.ReactNode }[]
}) {
  return (
    <AppointmentsSheetSectionCard
      title={title}
      body={
        <div className="divide-y rounded-md border px-3">
          {rows.map((r) => (
            <MetaRow key={r.label} label={r.label} value={r.value} />
          ))}
        </div>
      }
    />
  )
}

function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

function formatBookedOn(value: string | null): string | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })
}

/**
 * Appointments details sheet — renders the selected row (from the DataTable
 * context via the `sheetSlot` prop) into titled section cards. All fields are
 * already on the row, so no detail fetch is needed.
 */
export function AppointmentsSheetWrapper({
  onClose,
  requestError,
  onRetry,
}: {
  requestError?: Error | null
  onRetry?: () => void
  /**
   * Called when the user explicitly dismisses the sheet (X / ESC / backdrop) —
   * NOT when `useSheetRowNavigation` drops a selection it can no longer
   * resolve. The caller uses it to clear the deep-link id, and that recovery
   * path must not be mistaken for a deliberate close: it fires while the
   * deep-linked row is still being fetched.
   */
  onClose?: () => void
} = {}) {
  const canViewClients = useCapability('data.clients.view') === true
  const { table, rowSelection, isLoading } = useDataTable<
    AppointmentRecord,
    unknown
  >()

  const {
    selectedRowKey,
    selectedRow,
    prevId,
    nextId,
    onPrev,
    onNext,
    onOpenChange,
  } = useSheetRowNavigation(table, rowSelection, !!isLoading || !!requestError)

  const handleOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (!isOpen) onClose?.()
      onOpenChange(isOpen)
    },
    [onClose, onOpenChange]
  )

  const appointment = selectedRow?.original
  const date = appointment
    ? formatAppointmentDate(appointment.source_appointment_date)
    : null
  const startTime = appointment
    ? formatAppointmentTime(appointment.start_time)
    : null
  const endTime = appointment
    ? formatAppointmentTime(appointment.end_time)
    : null
  const title =
    [date, startTime].filter(Boolean).join(' · ') || 'Appointment Details'

  // Copy a shareable link to this appointment — the same `?…id=<uuid>&
  // details=1` shape the list reads back on load. `window.location.search` is
  // carried over so a link shared from a filtered view lands on that view.
  const handleCopyAppointmentLink = React.useCallback(() => {
    const appointmentId = appointment?.id
    if (!appointmentId) return
    const params = new URLSearchParams(window.location.search)
    params.set(APPOINTMENT_ID_PARAM, appointmentId)
    params.set('details', '1')
    const appointmentDate = getAppointmentDate(appointment)
    if (params.get('appointments.view') === 'calendar' && appointmentDate) {
      params.set('appointments.date', format(appointmentDate, 'yyyy-MM-dd'))
    }
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`
    if (!navigator.clipboard) {
      toast.error('Failed to copy link')
      return
    }
    navigator.clipboard.writeText(url).then(
      () => toast.success('Link to this appointment copied'),
      () => toast.error('Failed to copy link')
    )
  }, [appointment])

  const additionalInfoRows = React.useMemo(() => {
    const info = appointment?.additional_info
    if (!info) return []
    return Object.entries(info).map(([key, value]) => ({
      label: humanizeKey(key),
      value:
        value === null || value === undefined
          ? null
          : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value),
    }))
  }, [appointment])

  return (
    <Sheet open={!!selectedRowKey} onOpenChange={handleOpenChange}>
      <SheetContent
        onCloseAutoFocus={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="bg-neutral-ui-bg w-full max-w-none gap-0 overflow-y-auto rounded-l-xl p-0 sm:max-w-lg"
        showCloseButton={false}>
        <SheetHeader className="bg-neutral-ui-bg sticky top-0 z-10">
          <div className="flex items-start justify-between gap-2">
            <SheetTitle className="min-w-0 flex-1 truncate text-left text-lg font-semibold">
              {title}
            </SheetTitle>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={!prevId}
              onClick={onPrev}
              aria-label="Previous appointment"
              title="Previous appointment (↑)">
              <ChevronUp className="h-5 w-5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={!nextId}
              onClick={onNext}
              aria-label="Next appointment"
              title="Next appointment (↓)">
              <ChevronDown className="h-5 w-5" />
            </Button>
            <SheetClose asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </SheetClose>
          </div>
          {appointment?.appointment_type ? (
            <div className="text-neutral-low-contrast text-sm">
              {appointment.appointment_type}
            </div>
          ) : null}
        </SheetHeader>

        <SheetDescription className="sr-only">
          Selected appointment details
        </SheetDescription>

        <div className="px-4 pt-0 pb-4">
          {appointment && isOpenSlotRow(appointment) ? (
            // Open-slot rows carry no patient/source data and no appointment
            // id to link to — show only what a slot has.
            <MetaCard
              title="Open Slot"
              rows={[
                { label: 'Provider', value: appointment.provider_name },
                { label: 'Location', value: appointment.location },
                {
                  label: 'Status',
                  value: appointment.status ? (
                    <AppointmentStatusBadge status={appointment.status} />
                  ) : null,
                },
                {
                  label: 'Time',
                  value:
                    [startTime, endTime].filter(Boolean).join(' – ') || null,
                },
                {
                  label: appointment.additional_info?.[
                    'appointment_interval_id'
                  ]
                    ? 'Appointments Available'
                    : 'Slots Open',
                  value: String(
                    (appointment.additional_info?.['appointment_interval_id']
                      ? appointment.additional_info?.['available_capacity']
                      : appointment.additional_info?.['timeslot_count']) ?? ''
                  ),
                },
              ]}
            />
          ) : appointment ? (
            <div className="space-y-4">
              <MetaCard
                title="Appointment Details"
                rows={[
                  {
                    label: 'Patient Name',
                    value:
                      appointment.patient_id && canViewClients ? (
                        <RouterLink
                          className="text-primary underline underline-offset-4"
                          to={patientDetailsHref(appointment.patient_id)}>
                          {appointment.patient_name || 'View client'}
                        </RouterLink>
                      ) : (
                        appointment.patient_name
                      ),
                  },
                  {
                    label: 'Status',
                    value: appointment.status ? (
                      <AppointmentStatusBadge status={appointment.status} />
                    ) : null,
                  },
                  {
                    label: 'Appointment Reason',
                    value: appointment.appointment_reason,
                  },
                  { label: 'Start Time', value: startTime },
                  { label: 'End Time', value: endTime },
                  // Imported records' created_at is ingestion time, not booking time.
                  ...(['careforce', 'mercury'].includes(
                    appointment.source_name ?? ''
                  )
                    ? [
                        {
                          label: 'Booked On',
                          value: formatBookedOn(appointment.created_at),
                        },
                      ]
                    : []),
                ]}
              />
              <AppointmentsClientIdentifiers
                patientId={appointment.patient_id}
              />
              <MetaCard
                title="Source / External System"
                rows={[
                  {
                    label: 'Source Appointment ID',
                    value: appointment.source_appointment_id,
                  },
                  {
                    label: 'Source Name',
                    value: sourceDisplayName(appointment.source_name),
                  },
                  { label: 'Source Status', value: appointment.source_status },
                  { label: 'MRN', value: appointment.mrn },
                  {
                    label: 'Source Patient ID',
                    value: appointment.source_patient_id,
                  },
                ]}
              />
              {additionalInfoRows.length > 0 ? (
                <MetaCard title="Additional Info" rows={additionalInfoRows} />
              ) : null}

              {/* Copy a shareable link to this appointment */}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleCopyAppointmentLink}>
                <Link className="mr-2 h-4 w-4" />
                Copy link
              </Button>
            </div>
          ) : requestError ? (
            <div className="space-y-2 py-8 text-center">
              <p className="text-muted-foreground text-sm">
                Unable to load appointment.
              </p>
              <Button variant="outline" onClick={onRetry}>
                Retry
              </Button>
            </div>
          ) : isLoading ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Loading…
            </p>
          ) : (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Select a row to view details
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
